import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import {
  Component,
  ElementRef,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import {DialogData, DialogValidationComponent} from '@Common/widgets/general/dialog-validation';
import {MatDialog} from '@angular/material/dialog';
import {IPageFlatNode, IPageNode} from './page.model';
import {ChecklistDatabase} from './page-database.service';
import {SubSink} from '@Common/core/utils/subsink';

@Component({
  selector: 'app-tree',
  templateUrl: './tree.component.html',
  styleUrls: ['./tree.component.scss'],
  providers: [ChecklistDatabase]
})

export class TreeComponent implements OnInit, OnDestroy {
  /**
   * mode édition : le drag & drop, l'édit et la suppression sont accessibles
   * sinon uniquement l'arbre en consultation
   */
  @Input() editMode = true;
  @Output() action = new EventEmitter();
  @Output() action2 = new EventEmitter();
  @Output() onClick = new EventEmitter();
  @Input() parents;

  /** Map from flat node to nested node. This helps us finding the nested node to be modified */
  flatNodeMap = new Map<IPageFlatNode, IPageNode>();

  /** Map from nested node to flattened node. This helps us to keep the same object for selection */
  nestedNodeMap = new Map<IPageNode, IPageFlatNode>();

  selectedParent: IPageFlatNode | null = null;

  newItemName = '';

  treeControl: FlatTreeControl<IPageFlatNode>;

  treeFlattener: MatTreeFlattener<IPageNode, IPageFlatNode>;

  dataSource: MatTreeFlatDataSource<IPageNode, IPageFlatNode>;

  /** The selection for checklist */
  checklistSelection = new SelectionModel<IPageFlatNode>(true /* multiple */);

  /* Drag and drop */
  dragNode: any;
  dragNodeExpandOverWaitTimeMs = 300;
  dragNodeExpandOverNode: any;
  dragNodeExpandOverTime: number;
  dragNodeExpandOverArea: string;
  @ViewChild('emptyItem', { static: true }) emptyItem: ElementRef;
  selectedNode = null;
  subSink = new SubSink();

  constructor(private database: ChecklistDatabase, public dialog: MatDialog) {}

  ngOnInit() {
    this.database.edit = this.editMode;
    this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel, this.isExpandable, this.getChildren);
    this.treeControl = new FlatTreeControl<IPageFlatNode>(this.getLevel, this.isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
    this._initDataSource();
    this.subSink.sink = this.database.dataChange.subscribe(data => {
      this.dataSource.data = [];
      this.dataSource.data = data;
    });
  }
  _initDataSource() {
    if (this.parents) {
      this.dataSource.data = this.parents;
      this.database.initialize(this.parents);
    }
  }
  getLevel = (node: IPageFlatNode) => node.level;

  isExpandable = (node: IPageFlatNode) => node.expandable;

  getChildren = (node: IPageNode): IPageNode[] => node.children;

  hasChild = (_: number, _nodeData: IPageFlatNode) => _nodeData.expandable;

  hasNoContent = (_: number, _nodeData: IPageFlatNode) => _nodeData.item === '';

  /**
   * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
   */
  transformer = (node: IPageNode, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode = existingNode && existingNode.item === node.item
      ? existingNode
      : new IPageFlatNode();
    flatNode.item = node.item;
    flatNode.level = level;
    flatNode.expandable = (node.children && node.children.length > 0);
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  }

  /** Whether all the descendants of the node are selected */
  descendantsAllSelected(node: IPageFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    return descendants.every(child => this.checklistSelection.isSelected(child));
  }

  /** Whether part of the descendants are selected */
  descendantsPartiallySelected(node: IPageFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some(child => this.checklistSelection.isSelected(child));
    return result && !this.descendantsAllSelected(node);
  }
  onClickLevel(event, node) {
    this.selectedNode = node;
    this.onClick.emit(node);
  }
  /** Toggle the to-do item selection. Select/deselect all the descendants node */
  todoItemSelectionToggle(node: IPageFlatNode): void {
    this.checklistSelection.toggle(node);
    const descendants = this.treeControl.getDescendants(node);
    this.checklistSelection.isSelected(node)
      ? this.checklistSelection.select(...descendants)
      : this.checklistSelection.deselect(...descendants);
  }

  /** Select the category so we can insert the new item. */
  addNewItem(node: IPageFlatNode) {
    const parentNode = this.flatNodeMap.get(node);
    this.database.insertItem(parentNode, '');
    this.treeControl.expand(node);
  }

  /** Save the node to database */
  saveNode(node: IPageFlatNode, itemValue: string) {
    const nestedNode = this.flatNodeMap.get(node);
    this.database.updateItem(nestedNode, itemValue);
  }

  handleDragStart(event, node) {
    // Required by Firefox (https://stackoverflow.com/questions/19055264/why-doesnt-html5-drag-and-drop-work-in-firefox)
    event.dataTransfer.setData('foo', 'bar');
    event.dataTransfer.setDragImage(this.emptyItem.nativeElement, 0, 0);
    this.dragNode = node;
    this.treeControl.collapse(node);
  }

  handleDragOver(event, node) {

    event.preventDefault();

    // Handle node expand
    if (node === this.dragNodeExpandOverNode) {
      if (this.dragNode !== node && !this.treeControl.isExpanded(node)) {
        if ((new Date().getTime() - this.dragNodeExpandOverTime) > this.dragNodeExpandOverWaitTimeMs) {
          this.treeControl.expand(node);
        }
      }
    } else {
      this.dragNodeExpandOverNode = node;
      this.dragNodeExpandOverTime = new Date().getTime();
    }

    // Handle drag area
    const percentageX = event.offsetX / event.target.clientWidth;
    const percentageY = event.offsetY / event.target.clientHeight;
    if (percentageY < 0.25) {
      this.dragNodeExpandOverArea = 'above';
    } else if (percentageY > 0.75) {
      this.dragNodeExpandOverArea = 'below';
    } else {
      this.dragNodeExpandOverArea = 'center';
    }
  }

  handleDrop(event, node) {
    event.preventDefault();
    if (node !== this.dragNode) {
      let newItem: IPageNode;
      if (this.dragNodeExpandOverArea === 'above') {
        newItem = this.database.copyPasteItemAbove(this.flatNodeMap.get(this.dragNode), this.flatNodeMap.get(node));
      } else if (this.dragNodeExpandOverArea === 'below') {
        newItem = this.database.copyPasteItemBelow(this.flatNodeMap.get(this.dragNode), this.flatNodeMap.get(node));
      } else {
        newItem = this.database.copyPasteItem(this.flatNodeMap.get(this.dragNode), this.flatNodeMap.get(node));
      }

      const parent = this.database.getParentFromNodes(newItem);
      this.database.deleteItem(this.flatNodeMap.get(this.dragNode));
      this.treeControl.expandDescendants(this.nestedNodeMap.get(newItem));
      this.action.emit({nodeId: newItem.item.id, parentId: (parent ? parent.item.id : 1), action: 'move'});
      this.action2.emit(this.database.dataChange.value);
    }

    this.dragNode = null;
    this.dragNodeExpandOverNode = null;
    this.dragNodeExpandOverTime = 0;
  }

  handleDragEnd(event) {
    this.dragNode = null;
    this.dragNodeExpandOverNode = null;
    this.dragNodeExpandOverTime = 0;
  }

  editNode(node: IPageNode) {
    this.action.emit({node: node, id: node.id, action: 'edit'});
  }

  addNode(node: IPageNode) {
    this.action.emit({node: node, id: node.id, action: 'add'});
  }

  /*deleteNode(node: IPageFlatNode){
    this.database.deleteItem(this.flatNodeMap.get(node));
    this.action.emit({node: node, id: node.item.id, action: 'delete'});
  }*/

  deleteNode(node: IPageFlatNode) {
    let name = '';
    const data = new DialogData();

    if (node.item && node.item.data && node.item.data.title) {
      name = node.item.data.title;
    }

    data.title = `Suppression de page`;
    data.message = `Voulez-vous vraiment supprimer "${name}" ?`;
    const modalConfirm = this.dialog.open(DialogValidationComponent, { data: data, disableClose: true});
    modalConfirm.updatePosition({top: '40px'});
    modalConfirm.afterClosed().subscribe(result => {
      if (result) {
        this.database.deleteItem(this.flatNodeMap.get(node));
        this.action.emit({node: node, id: node.item.id, action: 'delete'});
      }
    });
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }
}
